/**
 * Axios API client for the aiChurchFlow backend (FastAPI People Service).
 *
 *  - Attaches the JWT access token to every request.
 *  - On a 401 (expired/invalid token), clears credentials and broadcasts
 *    LOGOUT_EVENT so the AuthProvider drops React state. The backend issues
 *    a single 24h token with no refresh, so we simply re-login.
 *  - Normalizes FastAPI errors ({ detail }) into Error(message).
 */
import axios from 'axios';
import { API_BASE_URL, TOKEN_KEY, LOGOUT_EVENT } from '../config';

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((cfg) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

client.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error.response?.status;
    const isLogin = error.config?.url?.includes('/auth/login');
    if (status === 401 && !isLogin) {
      localStorage.removeItem(TOKEN_KEY);
      window.dispatchEvent(new Event(LOGOUT_EVENT));
    }
    return Promise.reject(error);
  }
);

function toError(error) {
  const detail = error.response?.data?.detail;
  let msg;
  if (Array.isArray(detail)) {
    // FastAPI validation errors: [{loc, msg, ...}]
    msg = detail.map((d) => d.msg).join('; ');
  } else {
    msg = detail || error.message || 'Request failed. Please try again.';
  }
  const e = new Error(msg);
  e.status = error.response?.status;
  return e;
}

export async function get(url, config) {
  try {
    return (await client.get(url, config)).data;
  } catch (e) {
    throw toError(e);
  }
}

export async function post(url, data, config) {
  try {
    return (await client.post(url, data, config)).data;
  } catch (e) {
    throw toError(e);
  }
}

export async function patch(url, data, config) {
  try {
    return (await client.patch(url, data, config)).data;
  } catch (e) {
    throw toError(e);
  }
}

export async function del(url, config) {
  try {
    return (await client.delete(url, config)).data;
  } catch (e) {
    throw toError(e);
  }
}

export default client;

/** Download an authenticated file (CSV export/template): fetch as a blob, then save it. */
export async function download(url, filename) {
  try {
    const res = await client.get(url, { responseType: 'blob' });
    const href = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  } catch (e) {
    throw toError(e);
  }
}

/** Multipart upload. The JSON default header would make axios serialize FormData as JSON. */
export async function upload(url, formData) {
  try {
    return (await client.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  } catch (e) {
    const err = toError(e);
    err.detail = e.response?.data?.detail;
    throw err;
  }
}
